import { OrgsClient } from "../proto/components_grpc_web_pb"
import {
  Org,
  Salesforce,
  ListOrgRequest,
  GetOrgRequest,
  CreateOrgRequest,
  DeleteOrgRequest,
  HubSpot,
  UpdateRequest,
  DeleteCompanyRequest
} from "../proto/components_pb"
import { frontendProxy } from "../../configs"
import { cleanUndefinedFromObject } from "./utils"

export default class OrgsAPI {
  /**
   * init the components api hook via grpc to components service
   */
  constructor(jwt) {
    if (jwt) {
      this.jwt = jwt
      this.baseMetadata = { authorization: `Bearer ${jwt}` }
      this.client = new OrgsClient(frontendProxy)
    } else {
      throw new Error("jwt is required")
    }
  }

  /**
   * Get all org
   *
   * @returns {Promise.<*>} - promise of list of orgs.
   */
  list() {
    // request
    const self = this
    const request = new ListOrgRequest()

    // make request -> orgs list
    return new Promise((resolve, reject) => {
      self.client.list(request, this.baseMetadata, (err, res) => {
        err
          ? reject(err)
          : resolve(
              res.getOrgsList().map((org) => {
                const obj = org.toObject()
                cleanUndefinedFromObject(obj)
                return obj
              })
            )
      })
    })
  }

  /**
   * Get specific Org.
   * @param {string} orgId - org's id
   * @returns {Promise.<*>} - promise of org.
   */
  get(orgId) {
    // request
    const self = this

    // set request
    const request = new GetOrgRequest()
    request.setId(orgId)

    // make request -> org
    return new Promise((resolve, reject) => {
      self.client.get(request, this.baseMetadata, (err, res) => {
        err ? reject(err) : resolve(cleanUndefinedFromObject(res.toObject()))
      })
    })
  }

  /**
   * Create org
   *
   * @param {object} org - org object
   * @returns {Promise.<*>} - promise
   */
  create(org) {
    // request
    const self = this

    // get request
    const request = this._getOrgObject(org)

    // make request -> org
    return new Promise((resolve, reject) => {
      self.client.create(request, this.baseMetadata, (err, res) => {
        err ? reject(err) : resolve(res.toObject())
      })
    })
  }

  /**
   * Update org
   */
  update(id, org) {
    // request
    const self = this

    const request = new UpdateRequest()
    request.setId(id)

    const orgRequest = this._getOrgObject(org, "update")
    request.setOrg(orgRequest)

    // make request -> org
    return new Promise((resolve, reject) => {
      self.client.update(request, this.baseMetadata, (err, res) => {
        err ? reject(err) : resolve(res.toObject())
      })
    })
  }

  /**
   * Delete org
   *
   * @param {string} orgId - org's id
   */
  delete(orgId) {
    // request
    const self = this

    // set request
    const request = new DeleteOrgRequest()
    request.setId(orgId)

    // make request -> org
    return new Promise((resolve, reject) => {
      self.client.delete(request, this.baseMetadata, (err, res) => {
        err ? reject(err) : resolve(res.toObject())
      })
    })
  }

  /**
   * Delete Company with all the users related
   * to the company, include firebase auth of
   * all the users.
   *
   * @param {string} orgId - the org id
   */
  deleteCompany(orgId) {
    // extract content
    const self = this

    // define request
    const request = new DeleteCompanyRequest()

    // set orgid
    request.setOrgId(orgId)

    // make request -> org
    return new Promise((resolve, reject) => {
      self.client.deleteCompany(request, this.baseMetadata, (err, res) => {
        err ? reject(err) : resolve(res.toObject())
      })
    })
  }

  /**
   * Get org request object
   *
   * @param {object} org - org's object
   * @returns {Org} - proto Org request object.
   */
  _getOrgObject(org, requestType) {
    const {
      name,
      releaiVersion,
      vendor,
      salesforce,
      hubspot,
      tos,
      privacy,
      claraNumber,
      botLang,
      zapier
    } = org

    // request org
    const requestOrg = new CreateOrgRequest()

    // set request
    const orgObj = new Org()

    // set properties
    if (name) orgObj.setName(name)
    if (releaiVersion) orgObj.setReleaiVersion(releaiVersion)
    if (vendor) orgObj.setVendor(vendor)

    // set salesforce if exists
    if (salesforce) {
      const salesforceReq = new Salesforce()
      salesforceReq.setApiKey(salesforce.api_key)
      orgObj.setSalesforce(salesforceReq)
    }

    // set hubspot if exsits
    if (hubspot) {
      const hubspotReq = new HubSpot()
      hubspotReq.setApiKey(hubspot.api_key)
      orgObj.setHubspot(hubspotReq)
    }

    // set zapier if exsits
    if (zapier) {
      let map = orgObj.getZapierMap()
      Object.entries(zapier).forEach(([k, v]) => {
        map.set(k, v)
      })
    }

    if (tos) orgObj.setTos(tos)
    if (privacy) orgObj.setPrivacy(privacy)
    if (claraNumber) orgObj.setClaraNumber(claraNumber)
    if (botLang) orgObj.setBotLang(botLang)

    requestOrg.setOrg(orgObj)
    return requestType === "update" ? orgObj : requestOrg
  }
}
